<script setup>
import { useField } from 'vee-validate'
import Birthday from '@/assets/icons/birthday.svg'
import Eye from '@/assets/icons/eye.svg'
import EyeClosed from '@/assets/icons/eye-closed.svg'
import Scan from '@/assets/icons/scan.svg'
import Success from '@/assets/icons/success.svg'
import FormFieldMessage from '@/components/Form/FormFieldMessage.vue'
import LoadingSpinner from '@/components/Base/LoadingSpinner.vue'

const props = defineProps({
    inputConfig: {
        type: Object,
        required: false,
        default: () => ({}),
        validator: (rawValue) => {
            const validations = {
                id: value => value.id === undefined || typeof value.id === 'string',
                label: value => value.label === undefined || typeof value.label === 'string',
                name: value => value.name === undefined || typeof value.name === 'string',
                fieldType: value => value.fieldType === undefined || typeof value.fieldType === 'string',
                readonly: value => value.readonly === undefined || (typeof value.readonly === 'string' && value.readonly === 'readonly'),
                required: value => value.required === undefined || (typeof value.required === 'string' && value.required === 'required'),
                includeToggle: value => value.includeToggle === undefined || typeof value.includeToggle === 'boolean',
                placeholder: value => value.placeholder === undefined || typeof value.placeholder === 'string',
                pending: value => value.pending === undefined || typeof value.pending === 'boolean',
                val: value => value.val === undefined || typeof value.val === 'string',
                validationRules: value => value.validationRules === undefined || typeof value.validationRules === 'object',
                helperText: value => value.helperText === undefined || typeof value.helperText === 'string',
                lightMode: value => value.lightMode === undefined || typeof value.lightMode === 'boolean',
                maxLength: value => value.maxLength === undefined || typeof value.maxLength === 'number',
                upperCase: value => value.upperCase === undefined || typeof value.upperCase === 'boolean',
                scanner: value => value.scanner === undefined || typeof value.scanner === 'boolean',
                large: value => value.large === undefined || typeof value.large === 'boolean',
                center: value => value.center === undefined || typeof value.center === 'boolean',
                emitTarget: value => value.emitTarget === undefined || typeof value.emitTarget === 'boolean',
                clickable: value => value.clickable === undefined || typeof value.clickable === 'boolean',
                serverError: value => value.serverError === undefined || typeof value.serverError === 'object',
                visualFeedback: value => value.visualFeedback === undefined || typeof value.visualFeedback === 'boolean',
                successIcon: value => value.successIcon === undefined || typeof value.successIcon === 'boolean',
                successLabel: value => value.successLabel === undefined || typeof value.successLabel === 'string'
            }

            // Iterate through all validation checks
            return Object.entries(validations).every(([_, validation]) => validation(rawValue))
        }
    }
})

// Define input ref
const input = ref(null)

// Check if input is date
const isDate = computed(() => props.inputConfig?.fieldType === 'date')

// Toggle password
const showPassword = ref(false)

const isPassword = computed(() => props.inputConfig?.fieldType === 'password')

const togglePassword = () => {
    showPassword.value = !showPassword.value
    input.value.type = showPassword.value ? 'text' : 'password'
}

// UseField
const { value, errorMessage, meta, resetField, validate } = useField(() =>
    props.inputConfig?.name, props.inputConfig?.validationRules
)

// Show error + success classes in case fields have validation rules
const requiresValidation = computed(() => props.inputConfig?.validationRules !== undefined)
const isValid = computed(() => meta.valid && meta.dirty && requiresValidation.value)

// Compute isError property
const isServerError = computed(() => props.inputConfig?.serverError?.error)
const isError = computed(() => (errorMessage.value && (!meta.valid) && requiresValidation.value))

// Show error + success classes in case fields have validation rules
const formFieldClass = computed(() => {
    return {
        'form-error': isError.value || isServerError.value,
        'form-success': isValid.value,
        'form-light-mode': props.inputConfig?.lightMode,
        'form-large-input': props.inputConfig?.large,
        'form-centered-input': props.inputConfig?.center,
    }
})

// Change initial value with dynamic data of input field
resetField({
    value: props.inputConfig?.val
})

// Use watcher to update data with dynamic values
watch(() => props.inputConfig?.val, () => {
    resetField({
        value: props.inputConfig?.val
    })
})

/**
 * Function to show the date picker.
 *
 * @return {undefined} No return value
 */
const showDatePicker = () => {
    input.value.focus()

    if (typeof HTMLInputElement.prototype.showPicker === 'function') {
        input.value.showPicker()
    }
}

/**
 * Function to handle click events based on inputConfig
 *
 */
const handleClickListener = () => {
    // Show datepicker
    if (props.inputConfig?.isDate) {
        showDatePicker()
    }
}

const handleAnimationEnd = (target) => {
    target.classList.remove('visual-feedback-effect')
}

/**
 * Handle input events and apply necessary modifications.
 *
 * @param {Object} target - the target element triggering the event
 */
const handleInputListener = (target) => {
    // Handle uppercase modifier while typing
    if (props.inputConfig?.upperCase) {
        resetField({ value: target.value.toUpperCase() })

        // Call validate function to trigger revalidation
        validate()
    }

    // Reset any server error messages while typing
    if (props.inputConfig?.errorMessage || props.inputConfig?.serverError) {
        emit('update:updateServerError', target)
    }
    // Emit target to parent component if data is valid
    if (props.inputConfig?.emitTarget && meta.valid) {
        // Handle uppercase modifier while emitting event
        target.value = props.inputConfig?.upperCase ? target.value.toUpperCase() : target.value

        emit('update:updateTarget', target)
    }
}

// Define emit function
const emit = defineEmits(['update:updateTarget', 'update:updateServerError', 'update:toggleBarcodeScannerVisibility'])
</script>

<template>
    <div
        class="form-input-text-wrapper"
        :class="formFieldClass"
        :data-readonly="inputConfig.readonly"
    >
        <label v-if="inputConfig.label" :for="inputConfig.id" class="form-input-text-label">
            {{ inputConfig.label }}
        </label>
        <div class="form-input-text-container">
            <input
                :id="inputConfig.id"
                ref="input"
                v-model="value"
                :readonly="inputConfig.readonly"
                :required="inputConfig.required"
                :name="inputConfig.name"
                :type="inputConfig.fieldType"
                :maxlength="inputConfig.maxLength"
                :placeholder="inputConfig.placeholder"
                :class="inputConfig?.visualFeedback ? 'visual-feedback-effect' : ''"
                class="form-input-text"
                @animationend="handleAnimationEnd($event.target)"
                @click="handleClickListener"
                @input="handleInputListener($event.target)"
            >

            <span
                v-if="isPassword && inputConfig.includeToggle"
                class="form-input-password-toggle"
                :class="showPassword ? 'show-password' : ''"
                @click="togglePassword"
            >
                <EyeClosed class="hide-password-icon" />
                <Eye class="show-password-icon" />
            </span>

            <span v-if="isDate" class="form-input-date-icon" :class="formFieldClass" @click="showDatePicker">
                <Birthday />
            </span>

            <span
                v-if="inputConfig.scanner"
                class="form-input-scanner-icon"
                @click="emit('update:toggleBarcodeScannerVisibility', true)"
            >
                <Scan />
            </span>

            <Transition name="fade">
                <span
                    v-if="inputConfig.successIcon"
                    class="form-input-success-icon"
                    :title="inputConfig.successLabel"
                >
                    <Success />
                </span>
            </Transition>

            <LoadingSpinner v-if="inputConfig.pending" class="form-loader" />
        </div>

        <FormFieldMessage
            :msg-config="{
                message: errorMessage || props.inputConfig?.serverError?.message,
                error: (errorMessage && !meta.valid) || isServerError
            }"
        />

        <span v-if="inputConfig.helperText" class="helper-text">
            {{ inputConfig.helperText }}
        </span>
    </div>
</template>

<style lang="scss">
.form-input-text-wrapper {
    margin-block-end: 20px;
    position: relative;
}

.form-input-text-container {
    position: relative;
}

.form-input-text-label {
    @include form-label;
}

.form-input-success-icon,
.form-input-scanner-icon,
.form-input-password-toggle,
.form-input-date-icon {
    @include form-icon;
}

// Safari only hack.. weird & ugly
@supports (-webkit-hyphens:none) {
    .form-input-date-icon {
        pointer-events: none;
        cursor: auto;
    }
}

.form-input-text {
    @include inputfield;
}

.helper-text {
    @include helper-text;
}

.show-password-icon {
    opacity: 1;
    position: static;
    pointer-events: all;
    transition: opacity 400ms ease;

    .show-password & {
        opacity: 0;
        position: fixed;
        pointer-events: none;
    }
}

.hide-password-icon {
    opacity: 0;
    position: fixed;
    pointer-events: none;
    transition: opacity 400ms ease;

    .show-password & {
        opacity: 1;
        position: static;
        pointer-events: all;
    }
}
</style>
